import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AgpmRepository } from '../../../store/agpm-state/agpm-state.repository';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { DynamicForm, FileRepresentation, SelectOption } from '../../../components/form-builder/form-builder.types';
import { AgpmService } from '../agpm.service';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { AgpmDetails, AgpmTaskDetails } from '@/types';
import { CurrentTasksService } from '../../current-tasks/current-tasks.service';
import { TextareaModule } from 'primeng/textarea';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { AgpmWorkflowBlocksService } from '../agpm-workflow-blocks.service';
import { BlockBuilderComponent } from '../../../components/block-builder/block-builder.component';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { AuthorizationService } from '@/services/authorization.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { getAgpmUpdateFormObject } from '../edit-notice/utils/edit-agpm-form-object';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { getAGPMDownloadDialog } from '../edit-notice/utils/agpm-download.dialog-object';
import { getAGPMUploadDialog } from '../edit-notice/utils/agpm-upload.dialog-object';
import { fileUtils } from '@/utils/file.utils';
import { url } from '@/utils/url.utils';

@Component({
  selector: 'sm-agpm-current-task',
  imports: [
    PageHeaderComponent,
    CommonModule,
    FormBuilderComponent,
    ButtonModule,
    TextareaModule,
    FormsModule,
    TranslateModule,
    BlockBuilderComponent,
    StatusComponent,
    DialogComponent,
  ],
  templateUrl: './task-details.component.html',
  styleUrl: './task-details.component.scss',
})
export class AgpmTaskDetailsComponent implements OnInit, OnDestroy {
  currentTasksService = inject(CurrentTasksService);
  agpmService = inject(AgpmService);
  agpmWorkflowBlocksService = inject(AgpmWorkflowBlocksService);
  agpmRepository = inject(AgpmRepository);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  fileService = inject(FileService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  authService = inject(AuthorizationService);
  translate = inject(TranslateService);

  breadcrumb = [
    {
      label: 'agpm.breadcrumb.agpm',
      routerLink: ['/agpm-current-notice'],
    },
    {
      label: '',
      routerLink: ['agpm-new-plan'],
    },
  ];

  taskId = this.activatedRoute.snapshot.params['taskId'];
  task?: AgpmTaskDetails;
  loading$ = new BehaviorSubject<boolean>(true);
  comment = new BehaviorSubject<string>('');
  acOptions: SelectOption[] = [];
  formObject: DynamicForm = getAgpmUpdateFormObject({
    store: this.agpmRepository,
    acOptions: this.acOptions,
    download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.AGPM),
    openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.AGPM),
    acDisabled: of(true),
  });
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  agpmDialogObject?: DialogObject;

  controlBlocks: DynamicBlocks[] = [];
  commentError: Observable<string> = of('');
  status = '';
  agpmDetails: AgpmDetails | null = null;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy() {
    this.agpmRepository.reset();
  }

  onCommentChange(value: string) {
    this.comment.next(value);
  }

  downloadModel() {
    this.fileService.download(
      {
        filename: this.agpmRepository.getNested('response', 'ref'),
        extension: 'pdf',
        alfresco_id: this.agpmRepository.getNested('response', 'modelAlfrescoId'),
      },
      NAMESPACE_ENUM.AGPM_MODEL
    );
  }

  loadData() {
    this.currentTasksService.getTaskDetailsAgpm(this.taskId).subscribe({
      next: response => {
        this.agpmDetails = response;
        this.agpmRepository.setResponseAndDto(response.agpm);
        this.status = response.agpm.status;
        this.currentTasksService.getTaskDetailsAgpm(this.taskId).subscribe((task: AgpmTaskDetails) => {
          this.breadcrumb[1].label = task.taskName;
          this.task = task;
          this.contractingAuthorityService.getCurrentUserContractingAuthority().subscribe(data => {
            this.acOptions = data.map(ac => {
              return { name: ac.nom, code: ac.id };
            });
            this.formObject = getAgpmUpdateFormObject({
              store: this.agpmRepository,
              acOptions: this.acOptions,
              isDisabled: of(
                (!response.isUpdatable && !!response.processInstanceId) || (response.isUpdatable == null && response.processInstanceId == null)
              ),
              acDisabled: of(true),
              download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.AGPM),
              openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.AGPM),
            });
          });
          //define blocks
          this.controlBlocks = !response.processInstanceId
            ? this.agpmWorkflowBlocksService.getDraftAgpmBlocks({
                refreshCount: response.agpm.nbrActualisation,
                save: () => this.updateAgpm(),
                startWorkflow: () => this.startWorkflow(),
                accessHistory: () => this.router.navigate(['agpm-version-history', response.agpm.id]),
              })
            : this.agpmWorkflowBlocksService.getFlowableAgpmBlocks({
                refreshCount: response.agpm.nbrActualisation,
                task: this.task,
                validate: (value: string) => this.validate(task, value, !response.isUpdatable),
                publish: () => this.publish(Number(response.taskId)),
                accessHistory: () => this.router.navigate(['agpm-version-history', response.agpm.id]),
                openDownloadDialog: () => this.validateAcStep('download'),
                openUploadDialog: () => this.validateAcStep('upload'),
                comment: this.comment,
                onCommentChange: (value: string) => this.onCommentChange(value),
              });
        });
        this.loading$.next(false);
      },
      error: () => {
        this.loading$.next(false);
      },
    });
  }

  validate(task: AgpmTaskDetails, value: string, commentIsRequired: boolean) {
    this.commentError = of('');
    const formData = this.agpmRepository.getDtoFormData(true);
    if (task.nextSteps.length > 1 && (!value || value === 'false') && !this.comment.getValue()) {
      this.commentError = of('Veuillez saisir un commentaire');
      return;
    }
    if (commentIsRequired) {
      if (value) formData.append('validate', value.toString());
    }
    formData.append('comment', this.comment.getValue());
    this.currentTasksService.validateAgpm(this.taskId, formData).subscribe({
      next: () => {
        this.comment.next('');
        this.agpmRepository.reset();
        this.router.navigate(['/current-tasks', { queryParams: { type: 'AGPM' } }]);
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: value ? this.translate.instant('agpm.message.success.validated') : this.translate.instant('agpm.message.success.rejected'),
        });
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.passedForValidation'),
        });
      },
    });
  }

  validateAcStep(variant: 'download' | 'upload') {
    if (variant === 'download')
      this.agpmDialogObject = getAGPMDownloadDialog({
        store: this.agpmRepository,
        validate: () => {
          this.agpmService
            .downloadAgpmModel({
              agpmId: this.agpmRepository.getNested('response', 'id'),
              agpmVer: this.agpmRepository.getNested('response', 'version'),
              signataire: this.agpmRepository.getNested('download', 'signPerson'),
              fonction: this.agpmRepository.getNested('download', 'function'),
            })
            .subscribe((blob: Blob) => {
              fileUtils.triggerBlobDownload(blob, `${this.agpmRepository.getNested('response', 'ref')}_modele.pdf`);
            });
          this.dialogComponent.toggleVisible();
        },
      });
    else {
      this.agpmDialogObject = getAGPMUploadDialog({
        store: this.agpmRepository,
        validate: () => {
          const modelAgpmFileAsArray = this.agpmRepository.getNested<File[]>('upload', 'modelAgpmFile');
          if (modelAgpmFileAsArray.length === 0) {
            this.messageService.add({
              severity: 'error',
              summary: 'Fichier manquant',
              detail: 'Veuillez sélectionner un fichier avant de passer pour validation',
            });
            return;
          }
          const formData = new FormData();
          const modelAgpmFile = modelAgpmFileAsArray[0];
          formData.append('validate', 'true');
          formData.append('comment', this.comment.getValue());
          formData.append('modelAgpmFile', modelAgpmFile);
          if (this.agpmDetails?.taskId) {
            this.agpmService.uploadAgpmModel(this.agpmDetails?.taskId, formData).subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Succès',
                  detail: 'Le AGPM a été passé pour validation avec succès',
                });
                this.dialogComponent.toggleVisible();
                url.reloadComponent(this.router);
              },
              error: err => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Erreur',
                  detail: err.error.message || "Une erreur est survenue lors de la passage du AGPM l'étape suivante",
                });
              },
            });
          }
        },
      });
    }
    this.dialogComponent.toggleVisible();
  }

  startWorkflow() {
    this.agpmService.startWorkflow(this.agpmRepository.getNested('response', 'id')).subscribe({
      next: () => {
        this.agpmRepository.reset();
        this.router.navigate(['current-tasks', { queryParams: { type: 'AGPM' } }]);
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('agpm.message.success.passedForValidation'),
        });
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.passedForValidation'),
        });
      },
    });
  }

  updateAgpm() {
    this.loading$.next(true);
    const agpmEditFormData = this.agpmRepository.getDtoFormData(true);

    this.currentTasksService.validateAgpm(this.taskId, agpmEditFormData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('agpm.message.success.edited'),
        });
        this.loading$.next(false);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.edited'),
        });
        this.loading$.next(false);
      },
    });
  }

  publish(taskId: number) {
    const formData = new FormData();
    formData.append('comment', this.comment.getValue());
    this.currentTasksService.validateAgpm(taskId, formData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('agpm.message.success.published'),
        });
        this.router.navigate(['agpm-current-notice']);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.published'),
        });
      },
    });
  }
}
